import { ButtonProps, Button, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import React from "react";
import { AavoTooltip } from "src/components/common/tooltips/AavoTooltip.tsx";
import { faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
import { resolveValueOrProvider, ValueOrProvider } from "src/utils/valueOrProvider.ts";
import { AavoIconButton } from "src/components/common/buttons/AavoIconButton.tsx";
import { logError } from "src/errorHandling/errorLogging.ts";

export interface AavoButtonProps
	extends Omit<ButtonProps, "startIcon" | "endIcon" | "disabled" | "onSubmit"> {
	loading?: boolean;
	label?: string;
	icon?: IconDefinition;
	endIcon?: IconDefinition;
	iconEl?: React.ReactNode;
	endIconEl?: React.ReactNode;
	disabled?: ValueOrProvider<boolean | string>;
	tooltip?: ValueOrProvider<string>;
}

export const AavoButton = React.forwardRef(
	(
		{
			loading = false,
			color,
			disabled: disabledValueProvider,
			tooltip: tooltipProvider,
			endIcon,
			icon,
			endIconEl,
			iconEl,
			children,
			label,
			sx,
			...other
		}: AavoButtonProps,
		ref: ButtonProps["ref"],
	) => {
		const disabled = resolveValueOrProvider(disabledValueProvider);
		const givenTooltip = resolveValueOrProvider(tooltipProvider);
		const tooltipTitle = givenTooltip
			? givenTooltip
			: typeof disabled === "string"
				? disabled
				: "";

		const isDisabled =
			loading || (typeof disabled === "string" ? disabled.length > 0 : disabled);

		if (!label && !children) {
			if (!icon) {
				logError("AavoButton must have either label or children or icon");
				return null;
			}

			return (
				<AavoTooltip title={tooltipTitle} arrow sx={sx}>
					<AavoIconButton
						ref={ref}
						color={color}
						disabled={isDisabled}
						icon={icon}
						loading={loading}
						sx={{
							flex: 1,
							...sx,
						}}
						{...other}
					/>
				</AavoTooltip>
			);
		}

		const startIconElement = loading ? (
			<FontAwesomeIcon icon={faSpinnerThird} spin={true} />
		) : (
			iconEl ?? (icon && <FontAwesomeIcon icon={icon} />)
		);

		const endIconElement = endIconEl ?? (endIcon && <FontAwesomeIcon icon={endIcon} />);

		return (
			<AavoTooltip
				title={tooltipTitle}
				arrow
				sx={sx}
			>
				<Button
					ref={ref}
					color={color}
					disabled={isDisabled}
					endIcon={endIconElement}
					startIcon={startIconElement}
					sx={{
						flex: 1,
						...sx,
					}}
					{...other}
				>
					{label ? (
						<Typography
							variant={"button"}
							sx={{
								flex: 1,
								textAlign: "left",
							}}
						>
							{label}
						</Typography>
					) : (
						children
					)}
				</Button>
			</AavoTooltip>
		);
	},
);
